import React, { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { streamAiTask } from '../../../utils/aiApi';
import AIFloatingButton from './AIFloatingButton';
import AISelectionToolbar from './AISelectionToolbar';
import {
  actionToStreamTask,
  applyHtmlToEditor,
  buildImprovePayload,
  editorHasContent,
  getCaretMetrics,
  getContentEndMetrics,
  getVisibleFallbackMetrics,
  replaceRangeWithHtml,
  toPreviewHtml,
} from './improveHelpers';
import './aiImprove.css';

const AIWritingPanel = lazy(() => import('./AIWritingPanel'));

/**
 * Host for Gemini-style Improve with AI inside the compose contentEditable.
 * Does not touch send logic. Lazy-loads the writing panel on first open.
 */
export default function AIImproveHost({
  editorRef,
  bodyHtml = '',
  disabled = false,
  onBodyChange,
}) {
  const [floatPos, setFloatPos] = useState({ top: 0, left: 0, visible: false });
  const [selectionUI, setSelectionUI] = useState({ top: 0, left: 0, visible: false });
  const [panelOpen, setPanelOpen] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [panelMounted, setPanelMounted] = useState(false);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [original, setOriginal] = useState('');
  const [aiText, setAiText] = useState('');
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState('');
  const [canUndo, setCanUndo] = useState(false);
  const [undoChip, setUndoChip] = useState(false);

  const metricsRef = useRef(null);
  const undoTimerRef = useRef(null);
  const lastActionRef = useRef(null);
  const abortRef = useRef(null);
  const undoHtmlRef = useRef('');
  const savedRangeRef = useRef(null);
  const rafRef = useRef(0);

  const syncBody = useCallback((html) => {
    onBodyChange?.(html);
  }, [onBodyChange]);

  const updateAnchors = useCallback(() => {
    const editor = editorRef?.current;
    if (!editor || disabled || panelOpen) {
      setFloatPos((p) => (p.visible ? { ...p, visible: false } : p));
      setSelectionUI((p) => (p.visible ? { ...p, visible: false } : p));
      return;
    }

    if (!editorHasContent(editor, bodyHtml)) {
      setFloatPos((p) => (p.visible ? { ...p, visible: false } : p));
      setSelectionUI((p) => (p.visible ? { ...p, visible: false } : p));
      metricsRef.current = null;
      return;
    }

    // Prefer caret → content end → sticky visible fallback (paste-safe).
    const metrics = getCaretMetrics(editor)
      || getContentEndMetrics(editor)
      || getVisibleFallbackMetrics(editor);
    if (!metrics) {
      setFloatPos((p) => (p.visible ? { ...p, visible: false } : p));
      setSelectionUI((p) => (p.visible ? { ...p, visible: false } : p));
      return;
    }

    metricsRef.current = metrics;
    if (metrics.hasSelection) {
      setSelectionUI({
        visible: true,
        top: Math.max(metrics.top - 44, 4),
        left: metrics.left,
      });
      setFloatPos((p) => (p.visible ? { ...p, visible: false } : p));
    } else {
      setSelectionUI((p) => (p.visible ? { ...p, visible: false } : p));
      setFloatPos({
        visible: true,
        top: metrics.top,
        left: metrics.left,
      });
    }
  }, [bodyHtml, disabled, editorRef, panelOpen]);

  // When React body state updates (paste → onInput), force a visible refresh.
  useEffect(() => {
    if (!bodyHtml || disabled || panelOpen) return;
    const id = requestAnimationFrame(updateAnchors);
    const t = setTimeout(updateAnchors, 80);
    return () => {
      cancelAnimationFrame(id);
      clearTimeout(t);
    };
  }, [bodyHtml, disabled, panelOpen, updateAnchors]);

  useEffect(() => {
    const schedule = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(updateAnchors);
    };

    // Paste/drop insert async — re-check after the browser updates the DOM.
    const scheduleAfterPaste = () => {
      schedule();
      setTimeout(schedule, 0);
      setTimeout(schedule, 50);
      setTimeout(schedule, 150);
      setTimeout(schedule, 300);
    };

    const editor = editorRef?.current;
    const host = editor?.closest('.compose-body-section');
    document.addEventListener('selectionchange', schedule);
    editor?.addEventListener('keyup', schedule);
    editor?.addEventListener('mouseup', schedule);
    editor?.addEventListener('input', scheduleAfterPaste);
    editor?.addEventListener('paste', scheduleAfterPaste);
    editor?.addEventListener('drop', scheduleAfterPaste);
    editor?.addEventListener('scroll', schedule);
    host?.addEventListener('scroll', schedule);
    window.addEventListener('resize', schedule);

    let observer;
    if (editor && typeof MutationObserver !== 'undefined') {
      observer = new MutationObserver(scheduleAfterPaste);
      observer.observe(editor, { childList: true, subtree: true, characterData: true });
    }

    schedule();
    return () => {
      cancelAnimationFrame(rafRef.current);
      observer?.disconnect();
      document.removeEventListener('selectionchange', schedule);
      editor?.removeEventListener('keyup', schedule);
      editor?.removeEventListener('mouseup', schedule);
      editor?.removeEventListener('input', scheduleAfterPaste);
      editor?.removeEventListener('paste', scheduleAfterPaste);
      editor?.removeEventListener('drop', scheduleAfterPaste);
      editor?.removeEventListener('scroll', schedule);
      host?.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, [editorRef, updateAnchors]);

  useEffect(() => () => {
    abortRef.current?.();
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
  }, []);

  const openPanel = (forceSelection = false) => {
    const metrics = metricsRef.current || getCaretMetrics(editorRef?.current);
    if (metrics?.range) savedRangeRef.current = metrics.range.cloneRange();
    metricsRef.current = metrics;
    setSelectionMode(Boolean(forceSelection || metrics?.hasSelection));
    setPanelMounted(true);
    setPanelOpen(true);
    setPreviewOpen(false);
    setError('');
    setAiText('');
  };

  const closePanel = () => {
    abortRef.current?.();
    setPanelOpen(false);
    setPreviewOpen(false);
    setLoading(false);
    setStreaming(false);
    setError('');
    requestAnimationFrame(updateAnchors);
  };

  const runAction = (action, extra = {}) => {
    const editor = editorRef?.current;
    if (!editor) return;

    const metrics = metricsRef.current || getCaretMetrics(editor);
    const selectionOnly = Boolean(selectionMode && metrics?.hasSelection);
    const sourceText = selectionOnly
      ? metrics.selectedText
      : (metrics?.fullText || editor.textContent || '');

    if (!sourceText.trim() && action !== 'continue') {
      setError('Add some text first.');
      setPreviewOpen(true);
      return;
    }

    const payload = buildImprovePayload(action, {
      text: action === 'continue'
        ? (metrics?.beforeCursor || sourceText)
        : sourceText,
      selectionOnly,
      targetLanguage: extra.targetLanguage,
      beforeCursor: metrics?.beforeCursor || sourceText,
      afterCursor: metrics?.afterCursor || '',
    });

    lastActionRef.current = { action, extra, payload };
    setOriginal(sourceText);
    setAiText('');
    setError('');
    setPreviewOpen(true);
    setLoading(true);
    setStreaming(true);

    abortRef.current?.();
    const task = actionToStreamTask(action);
    // Lazy: OpenRouter is only hit when the user picks an action.
    abortRef.current = streamAiTask(task, payload, {
      onDelta: (delta) => {
        setLoading(false);
        setAiText((prev) => prev + (delta || ''));
      },
      onDone: () => {
        setStreaming(false);
        setLoading(false);
      },
      onError: (err) => {
        setError(err.message || 'AI request failed');
        setStreaming(false);
        setLoading(false);
      },
    });
  };

  const applyResult = (mode) => {
    const editor = editorRef?.current;
    if (!editor || !aiText.trim()) return;

    undoHtmlRef.current = editor.innerHTML;
    setCanUndo(true);

    const html = toPreviewHtml(aiText);
    const selectionOnly = selectionMode && savedRangeRef.current;
    const isContinue = lastActionRef.current?.action === 'continue';

    if (isContinue || mode === 'append') {
      // Insert at saved caret / selection end (never wipe the whole draft for Continue).
      try {
        const range = savedRangeRef.current
          ? savedRangeRef.current.cloneRange()
          : null;
        if (range) {
          range.collapse(false);
          replaceRangeWithHtml(range, mode === 'append' && !isContinue ? `<br/>${html}` : html);
          syncBody(editor.innerHTML);
        } else {
          syncBody(applyHtmlToEditor(editor, html, 'append'));
        }
      } catch (_) {
        syncBody(applyHtmlToEditor(editor, html, 'append'));
      }
    } else if (mode === 'replace' && selectionOnly) {
      try {
        replaceRangeWithHtml(savedRangeRef.current, html);
        syncBody(editor.innerHTML);
      } catch (_) {
        syncBody(applyHtmlToEditor(editor, html, 'replace'));
      }
    } else {
      syncBody(applyHtmlToEditor(editor, html, 'replace'));
    }

    closePanel();
    setUndoChip(true);
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    undoTimerRef.current = setTimeout(() => {
      setUndoChip(false);
      setCanUndo(false);
    }, 8000);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(aiText);
    } catch (_) { /* ignore */ }
  };

  const handleUndo = () => {
    const editor = editorRef?.current;
    if (!editor || !undoHtmlRef.current) return;
    editor.innerHTML = undoHtmlRef.current;
    syncBody(undoHtmlRef.current);
    setCanUndo(false);
    setUndoChip(false);
    undoHtmlRef.current = '';
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
  };

  const handleRegenerate = () => {
    const last = lastActionRef.current;
    if (!last) return;
    runAction(last.action, last.extra || {});
  };

  return (
    <>
      <div className="aii-anchor-layer" aria-hidden={!floatPos.visible && !selectionUI.visible}>
        <AIFloatingButton
          visible={floatPos.visible && !disabled}
          top={floatPos.top}
          left={floatPos.left}
          disabled={disabled}
          onClick={() => openPanel(false)}
        />
        <AISelectionToolbar
          visible={selectionUI.visible && !disabled}
          top={selectionUI.top}
          left={selectionUI.left}
          disabled={disabled}
          onImprove={() => openPanel(true)}
        />
        {undoChip && canUndo && (
          <button
            type="button"
            className="aii-undo-chip"
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleUndo}
          >
            Undo AI change
          </button>
        )}
      </div>

      {panelMounted && (
        <Suspense fallback={null}>
          <AIWritingPanel
            open={panelOpen}
            selectionMode={selectionMode}
            onClose={closePanel}
            onRunAction={runAction}
            preview={{
              open: previewOpen,
              original,
              aiText,
              streaming,
              loading,
              error,
              canUndo,
              onReplace: () => applyResult('replace'),
              onAppend: () => applyResult('append'),
              onCopy: handleCopy,
              onRegenerate: handleRegenerate,
              onUndo: handleUndo,
              onCancel: () => {
                abortRef.current?.();
                setPreviewOpen(false);
                setAiText('');
                setError('');
                setLoading(false);
                setStreaming(false);
              },
            }}
          />
        </Suspense>
      )}
    </>
  );
}
